import { ComponentPropsWithoutRef, useMemo, useRef } from "react";
import * as styles from "./SectionBlockPinnedMediaItems.css";
import { SectionBlockPinnedMediaItems as IBlockPinnedMediaItems } from "~/sanity/types";
import { Heading } from "~/components/elements/Heading";
import { ButtonLink } from "~/components/elements/ButtonLink";
import Grid from "~/components/globals/Grid";
// @ts-expect-error — SVG module typings not declared for ?react
import ArrowOutward from "@material-design-icons/svg/filled/arrow_outward.svg?react";
import { Image, IImage } from "~/components/elements/Image";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import gsap from "gsap";
import PortableText, {
  IPortableText,
} from "~/components/portableText/PortableText";

export type SectionBlockPinnedMediaItemsProps =
  ComponentPropsWithoutRef<"div"> & {
    data: IBlockPinnedMediaItems;
  };

export const SectionBlockPinnedMediaItems = ({
  data,
}: SectionBlockPinnedMediaItemsProps) => {
  const { title, image, text, items } = data;

  const sectionRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);

  const pinnedImages = useMemo((): IImage[] => {
    if (!image) return [];
    const featured = image as IImage;
    return [
      featured,
      ...(items?.map((item) => (item.image as IImage) ?? featured) ?? []),
    ];
  }, [image, items]);

  useGSAP(
    () => {
      if (pinnedImages.length === 0) return;

      gsap.registerPlugin(ScrollTrigger);

      const mm = gsap.matchMedia();

      mm.add("(min-width: 768px)", () => {
        const trigger = triggerRef.current;
        const pin = pinRef.current;
        if (!trigger || !pin) return;

        const layers = gsap.utils.toArray<HTMLElement>(
          pin.querySelectorAll("[data-pin-layer]"),
        );
        if (layers.length === 0) return;

        const n = layers.length;
        const syncPinnedLayers = (progress: number) => {
          const idx = Math.min(n - 1, Math.floor(progress * n));
          layers.forEach((el, i) => {
            const active = i === idx;
            gsap.set(el, {
              opacity: active ? 1 : 0,
              pointerEvents: active ? "auto" : "none",
            });
          });
        };

        const itemCount = items?.length ?? 0;
        const st = ScrollTrigger.create({
          trigger,
          pin,
          start: "center center",
          end: `center+=${itemCount * 100}% center`,
          anticipatePin: 1,
          fastScrollEnd: 3000,
          onUpdate: (self) => syncPinnedLayers(self.progress),
        });

        syncPinnedLayers(st.progress);

        return () => {
          st.kill();
        };
      });

      return () => {
        mm.revert();
      };
    },
    { scope: sectionRef, dependencies: [pinnedImages.length, items?.length, image] },
  );

  return (
    <section ref={sectionRef} className={styles.wrapper}>
      <Grid className={styles.grid}>
        <div ref={triggerRef} className={styles.content}>
          <div className={styles.card}>
            <div className={styles.imageWrapperMobile}>
              <div className={styles.imageContainer({ featured: true })}>
                {image && (
                  <Image
                    image={image as IImage}
                    className={styles.image}
                    width={650}
                  />
                )}
              </div>
            </div>
            <div className={styles.text}>
              <Heading as="h2" size="hx" uppercase>
                {title}
              </Heading>
              {text && <PortableText blocks={text as IPortableText} />}
            </div>
          </div>
        </div>
        <div
          className={styles.imageWrapper}
          style={{ gridRow: items ? `1/${items?.length + 2}` : "auto" }}
        >
          {pinnedImages.length > 0 && (
            <div
              ref={pinRef}
              className={`${styles.imageContainer({ featured: false })} ${styles.pinStack}`}
            >
              {pinnedImages.map((img, index) => (
                <div
                  key={index}
                  className={styles.pinLayer}
                  data-pin-layer
                >
                  <Image
                    width={650}
                    image={img}
                    className={styles.image}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {items &&
          items.map((item, index) => {
            const { cardCallout, image } = item;
            const { title, text, links } = cardCallout || {};
            return (
              cardCallout && (
                <div className={styles.content} key={index}>
                  <div className={styles.card}>
                    <div className={styles.imageWrapperMobile}>
                      <div
                        className={styles.imageContainer({ featured: false })}
                      >
                        {image && (
                          <Image
                            image={image as IImage}
                            className={styles.image}
                            width={650}
                          />
                        )}
                      </div>
                    </div>
                    <div className={styles.text}>
                      <Heading as="h3" size="h3" bold uppercase>
                        {title}
                      </Heading>
                      {text && <PortableText blocks={text as IPortableText} />}
                    </div>
                    {links && (
                      <div className={styles.buttons}>
                        {links.map(
                          (link, index) =>
                            link &&
                            (link as { slug?: string }).slug && (
                              <ButtonLink
                                key={index}
                                theme={index % 2 == 0 ? "dark" : "outlineDark"}
                                link={link}
                                icons={
                                  index % 2 == 0
                                    ? { iconPrimary: <ArrowOutward /> }
                                    : undefined
                                }
                              >
                                {link.text}
                              </ButtonLink>
                            ),
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            );
          })}
      </Grid>
    </section>
  );
};
